import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SeoService } from '@core/services/seo.service';
import { environment } from '@environment';
import { ResizeObserverEntry } from '@juggle/resize-observer';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Job } from '@models/job/job';
import { JobFilter } from '@models/search/job-filter';
import { JobSearchContextEnum } from '@models/search/job-search-context.enum';
import { SeoLink } from '@models/seo/seo-link';
import { RouterState } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { GetJob } from '@store/job/jobs.actions';
import { JobsState } from '@store/job/jobs.state';
import { JobSearchLoad } from '@store/search/job-search/job-search.actions';
import { JobSearchState } from '@store/search/job-search/job-search.state';
import { SessionState } from '@store/session/session.state';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  @Select(SessionState.isLogged) isLogged$: Observable<boolean>;

  jobs$: Observable<Job[]>;
  loading: boolean;
  breadcrumbSegments$: Observable<SeoLink[]>;

  showMedia = false;
  showMenu = true;

  jobLocalization: string;
  jobLoaded = false;

  locale = environment.locale;
  LocaleEnum = LocaleEnum;

  readonly isMobile$: Observable<boolean>;
  readonly wizbiiDomain: string;

  constructor(
    route: ActivatedRoute,
    store: Store,
    private readonly seoService: SeoService,
    @Inject(PLATFORM_ID) private readonly platformId: Record<any, unknown>,
    private readonly cdr: ChangeDetectorRef,
    private readonly breakpointObserver: BreakpointObserver
  ) {
    this.wizbiiDomain = environment.apiDomain;
    this.showMedia = isPlatformBrowser(this.platformId);

    this.isMobile$ = this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
      startWith({ matches: true }),
      map((result) => result.matches),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.jobs$ = route.params.pipe(
      map((params) => params['jobId']),
      distinctUntilChanged(),
      switchMap((jobId) => store.dispatch(new GetJob(jobId)).pipe(map(() => jobId))),
      switchMap((jobId) => store.select(JobsState.job(jobId))),
      filter((job) => !!job),
      tap((job: Job) => {
        this.jobLoaded = true;
        this.jobLocalization = job?.location.departmentShort;
      }),
      map((job) => (job.company ? new JobFilter({ company: job.company.id }) : undefined)),
      switchMap((filterParam) =>
        (filterParam
          ? store.dispatch(new JobSearchLoad(JobSearchContextEnum.Company, filterParam))
          : of(undefined)
        ).pipe(map(() => filterParam))
      ),
      switchMap((filterParam) =>
        filterParam ? store.select(JobSearchState.result(JobSearchContextEnum.Company, 'hits')) : of(undefined)
      )
    );

    this.breadcrumbSegments$ = store.select(RouterState.state).pipe(
      filter((state: any) => state.params['jobId']),
      map((state: any) => state.params['jobId']),
      distinctUntilChanged(),
      switchMap((jobId) => this.seoService.getBreadcrumbSegmentsWithJob(jobId))
    );
  }

  onResize(ev: ResizeObserverEntry): void {
    this.showMenu = ev.contentRect.width < 133;

    this.cdr.detectChanges();
  }
}
